import { z } from 'zod';

/**
 * Единая схема аналитических событий (владелец — Блок 19). ADR-0084, PR#1 задача 0.2.
 *
 * Парная задаче 0.1 (ADR-файл). Фиксирует ТОЛЬКО форму события (конверт §1 +
 * discriminated union §2 + реестр `feature_use` §3). Write-API эндпоинт
 * (`POST /api/analytics/events`) и таблица D1 — задача 1.3; клиент-эмиттер с
 * гейтом `marketingAnalytics` + fail-safe — Фаза 2 (`apps/web`). В этом контракте
 * гейта согласия НЕТ — только форма + (см. `api-client/analytics.ts`) транспорт.
 *
 * ── Инвариант §1a (анти-спуфинг, P-009) ─────────────────────────────────────
 * Серверные поля `userId` / `region` / `ts` проставляет write-API из JWT и НЕ
 * доверяет клиенту. Поэтому две разные формы:
 *   - `AnalyticsEventInputSchema`  — то, что шлёт клиент: `{ eventId, type,
 *     payload, clientTs? }`. НЕ содержит `userId`/`region`/`ts` (тип-страховка от
 *     подделки событий от чужого userId/региона). Лишние серверные поля из тела
 *     клиента молча срезаются (default strip ZodObject) — сервер их игнорирует и
 *     перезаписывает авторитетными значениями (ADR §1a).
 *   - `AnalyticsEventSchema`       — хранимое: Input + сервер-конверт
 *     `{ userId, region, ts }`.
 *
 * `clientTs` (опц.) — время устройства ТОЛЬКО для упорядочивания внутри батча;
 * авторитетное время — серверный `ts` (ADR §1a).
 *
 * Все текстовые каталожные значения — **verbatim** (P-001): `productGlobalId`,
 * `query`, `subcategory`, `brand`, `service` НЕ нормализуются (нет regex/transform).
 *
 * References:
 *   - ADR-0084 §1/§1a/§2/§3
 *   - policies: P-009, P-010, P-031, P-001, P-011/P-012/P-013, P-035
 *   - паттерн discriminatedUnion: `contracts/feedback.ts`
 */

// ── Под-словари payload (enum-поля §2) ──────────────────────────────────────

/** Каталожные блоки 2/3/4/5. `hook` (крючки) — подвид Блока ручек. */
export const ProductKindSchema = z.enum(['decor', 'paint', 'handle', 'hook', 'furniture']);
export type ProductKind = z.infer<typeof ProductKindSchema>;

/** Область поиска (§2, Правка A): каталоги + партнёры/услуги. */
export const SearchScopeSchema = z.enum([
  'decor',
  'paint',
  'handle',
  'furniture',
  'partner',
  'service',
]);
export type SearchScope = z.infer<typeof SearchScopeSchema>;

/** Кому уходит контакт: дилеру (каталог) или партнёру (Блок 8). */
export const ContactTargetTypeSchema = z.enum(['dealer', 'partner']);
export type ContactTargetType = z.infer<typeof ContactTargetTypeSchema>;

/** Канал контакта-редиректа. `max` — клиентский эмит (ADR §4 Правка C). */
export const ContactChannelSchema = z.enum(['whatsapp', 'telegram', 'call', 'max']);
export type ContactChannel = z.infer<typeof ContactChannelSchema>;

/** Источник `session_start` (один на сессию, эмитит оболочка — ADR §4). */
export const SessionSourceSchema = z.enum(['pwa', 'web']);
export type SessionSource = z.infer<typeof SessionSourceSchema>;

/** Регион (P-010) — проставляет сервер из `users.region`. */
export const AnalyticsRegionSchema = z.enum(['KZ', 'RU', 'UZ']);
export type AnalyticsRegion = z.infer<typeof AnalyticsRegionSchema>;

// ── Реестр feature_use (§3 ADR) ─────────────────────────────────────────────

/**
 * Единый реестр id для `feature_use`. Владелец — Блок 19. Конвенция id:
 * `<block>.<screen>.<action>`. Писатель добавляет свой id PR-ом (со ссылкой на
 * экран); коллизии исключает единый список. На старте — минимальный набор из
 * примеров ADR §3; расширяется аддитивно.
 */
export const FEATURE_USE_REGISTRY = [
  'cooperation.form.open',
  'cabinet.subscription.open',
  'partners.list.filter',
] as const;

export type FeatureUseId = (typeof FEATURE_USE_REGISTRY)[number];

/** Тип-гард: значение принадлежит реестру `feature_use`. */
export function isFeatureUseId(value: string): value is FeatureUseId {
  return (FEATURE_USE_REGISTRY as readonly string[]).includes(value);
}

// ── Payload по типам (§2 ADR) ───────────────────────────────────────────────

/** Каталожные текстовые значения — verbatim (P-001), `.min(1)` без normalize. */
const verbatim = z.string().min(1);

export const CardViewPayloadSchema = z.object({
  productKind: ProductKindSchema,
  productGlobalId: verbatim,
  brand: verbatim,
});
export type CardViewPayload = z.infer<typeof CardViewPayloadSchema>;

export const BrandClickPayloadSchema = z.object({
  productKind: ProductKindSchema,
  brand: verbatim,
});
export type BrandClickPayload = z.infer<typeof BrandClickPayloadSchema>;

export const SubcategorySelectPayloadSchema = z.object({
  productKind: ProductKindSchema,
  subcategory: verbatim,
});
export type SubcategorySelectPayload = z.infer<typeof SubcategorySelectPayloadSchema>;

export const SearchPayloadSchema = z.object({
  scope: SearchScopeSchema,
  /** Поисковая строка товара — дословно (P-001), без ПДн (ADR §2). */
  query: z.string(),
  resultsCount: z.number().int().nonnegative(),
});
export type SearchPayload = z.infer<typeof SearchPayloadSchema>;

export const ShopContactPayloadSchema = z.object({
  targetType: ContactTargetTypeSchema,
  targetId: verbatim,
  channel: ContactChannelSchema,
  city: verbatim,
  /** Товарные поля nullable — контакт может быть вне карточки товара. */
  productKind: ProductKindSchema.nullable(),
  productGlobalId: verbatim.nullable(),
  brand: verbatim.nullable(),
});
export type ShopContactPayload = z.infer<typeof ShopContactPayloadSchema>;

export const PartnerCardViewPayloadSchema = z.object({
  partnerId: verbatim,
  /**
   * Каноничная «Услуга» Блока 8 (R1). Словаря-источника в
   * `packages/shared/src/domain/` пока нет (категория партнёра — free-text
   * `z.string()` в `contracts/partners.ts`), поэтому здесь `z.string().min(1)`
   * verbatim. TODO: заменить на enum/ссылку, когда появится словарь Блока 8
   * (НЕ выдумывать перечень). `'other'` — для free-text Сотрудничества (9).
   */
  service: verbatim,
  city: verbatim,
});
export type PartnerCardViewPayload = z.infer<typeof PartnerCardViewPayloadSchema>;

export const ServiceSelectPayloadSchema = z.object({
  service: verbatim, // см. PartnerCardViewPayloadSchema.service (R1, TODO Блок 8)
  city: verbatim.optional(),
});
export type ServiceSelectPayload = z.infer<typeof ServiceSelectPayloadSchema>;

export const SessionStartPayloadSchema = z.object({
  source: SessionSourceSchema.optional(),
});
export type SessionStartPayload = z.infer<typeof SessionStartPayloadSchema>;

export const FeatureUsePayloadSchema = z.object({
  /** Должно принадлежать `FEATURE_USE_REGISTRY` (§3). */
  feature: z.enum(FEATURE_USE_REGISTRY),
});
export type FeatureUsePayload = z.infer<typeof FeatureUsePayloadSchema>;

// ── Конверт (§1) и сборка union ─────────────────────────────────────────────

/** Поля конверта, которые шлёт клиент (§1/§1a). */
const inputEnvelope = {
  /** UUID — идемпотентность записи (шлёт клиент). */
  eventId: z.string().uuid(),
  /** Опц. время клиента ТОЛЬКО для упорядочивания батча (§1a). */
  clientTs: z.string().datetime().optional(),
};

/** Серверный конверт — проставляет write-API из JWT (§1a). Клиенту НЕ доверяется. */
const serverEnvelope = {
  userId: z.string().uuid(),
  region: AnalyticsRegionSchema,
  ts: z.string().datetime(),
};

/** Helper: ZodObject ветки входа `{ ...inputEnvelope, type, payload }`. */
function inputMember<T extends string, P extends z.ZodTypeAny>(type: T, payload: P) {
  return z.object({ ...inputEnvelope, type: z.literal(type), payload });
}

const cardViewInput = inputMember('card_view', CardViewPayloadSchema);
const brandClickInput = inputMember('brand_click', BrandClickPayloadSchema);
const subcategorySelectInput = inputMember('subcategory_select', SubcategorySelectPayloadSchema);
const searchInput = inputMember('search', SearchPayloadSchema);
const shopContactInput = inputMember('shop_contact', ShopContactPayloadSchema);
const partnerCardViewInput = inputMember('partner_card_view', PartnerCardViewPayloadSchema);
const serviceSelectInput = inputMember('service_select', ServiceSelectPayloadSchema);
const sessionStartInput = inputMember('session_start', SessionStartPayloadSchema);
const featureUseInput = inputMember('feature_use', FeatureUsePayloadSchema);

/** Форма запроса клиента (§1a) — БЕЗ серверных полей. */
export const AnalyticsEventInputSchema = z.discriminatedUnion('type', [
  cardViewInput,
  brandClickInput,
  subcategorySelectInput,
  searchInput,
  shopContactInput,
  partnerCardViewInput,
  serviceSelectInput,
  sessionStartInput,
  featureUseInput,
]);
export type AnalyticsEventInput = z.infer<typeof AnalyticsEventInputSchema>;

/** Хранимая форма (§1) — Input + серверный конверт `{ userId, region, ts }`. */
export const AnalyticsEventSchema = z.discriminatedUnion('type', [
  cardViewInput.extend(serverEnvelope),
  brandClickInput.extend(serverEnvelope),
  subcategorySelectInput.extend(serverEnvelope),
  searchInput.extend(serverEnvelope),
  shopContactInput.extend(serverEnvelope),
  partnerCardViewInput.extend(serverEnvelope),
  serviceSelectInput.extend(serverEnvelope),
  sessionStartInput.extend(serverEnvelope),
  featureUseInput.extend(serverEnvelope),
]);
export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;

/** Дискриминатор события (§2). */
export type EventType = AnalyticsEventInput['type'];
